import React from "react";
import { FaEdit, FaExternalLinkAlt, FaStar, FaTrash } from "react-icons/fa";

import {
  getCategoryLabel,
  getSectionStatus,
  resolveMediaUrl,
} from "../utils/sectionAdminUtils";

export default function SectionAdminTable({
  sections,
  categoriesById,
  loading,
  totalRows,
  visibleCount,
  demoMode,
  onLoadMore,
  onEdit,
  onDelete,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-white/10 sm:p-5 lg:p-6">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          Tableau des associations
        </h2>

        <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {totalRows.toLocaleString("fr-FR")} association
          {totalRows > 1 ? "s" : ""} trouvée{totalRows > 1 ? "s" : ""}.
        </p>
      </div>

      <div className="p-3 sm:p-5 lg:p-6">
        <div className="overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/60">
          <table className="min-w-[1250px] w-full text-sm">
            <thead className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900">
              <tr className="text-left">
                <th className="sticky left-0 z-40 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  Visuel
                </th>

                <th className="sticky left-[100px] z-40 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  Association
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Catégorie
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Responsable
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Contact
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Statut
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Ordre
                </th>

                <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-white/10">
              {loading ? (
                [...Array(8)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={8} className="px-4 py-3">
                      <div className="h-10 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                    </td>
                  </tr>
                ))
              ) : sections.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                  >
                    Aucune association ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                sections.map((section) => {
                  const status = getSectionStatus(section);
                  const categoryLabel = getCategoryLabel(section, categoriesById);

                  return (
                    <tr
                      key={section.id}
                      className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                    >
                      <td className="sticky left-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                        <div className="h-14 w-16 overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                          <img
                            src={resolveMediaUrl(section.photoURL)}
                            alt={section.nom_section}
                            className="h-full w-full object-cover"
                            onError={(event) => {
                              event.currentTarget.src =
                                "/images/asso_placeholder.webp";
                            }}
                          />
                        </div>
                      </td>

                      <td className="sticky left-[100px] z-20 bg-white px-4 py-3 dark:bg-slate-950">
                        <div className="flex items-start gap-3">
                          <img
                            src={resolveMediaUrl(
                              section.logo_section,
                              "/images/asso_placeholder.jpg"
                            )}
                            alt={`Logo ${section.nom_section}`}
                            className="h-10 w-10 shrink-0 rounded-2xl object-contain ring-1 ring-slate-200 dark:ring-slate-700"
                            onError={(event) => {
                              event.currentTarget.src =
                                "/images/asso_placeholder.webp";
                            }}
                          />

                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-900 dark:text-white">
                                {section.nom_section || "—"}
                              </p>

                              {section.is_featured && (
                                <FaStar
                                  className="shrink-0 text-amber-400"
                                  title="Mise en avant"
                                />
                              )}
                            </div>

                            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                              {section.discipline_section || "Discipline non renseignée"}
                            </p>

                            {section.lien_section && (
                              <a
                                href={section.lien_section}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-primary"
                              >
                                Voir la fiche
                                <FaExternalLinkAlt size={10} />
                              </a>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                          {categoryLabel}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        {section.responsable_section || "—"}
                      </td>

                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        <div className="space-y-1">
                          <p>{section.tel_section || "—"}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {section.mail_section || "—"}
                          </p>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={[
                            "inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1",
                            status.className,
                          ].join(" ")}
                        >
                          {status.label}
                        </span>
                      </td>

                      <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-300">
                        {section.ordre ?? 999}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onEdit(section)}
                            disabled={demoMode}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                            title="Modifier"
                          >
                            <FaEdit />
                          </button>

                          <button
                            type="button"
                            onClick={() => onDelete(section)}
                            disabled={demoMode}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Supprimer"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && visibleCount < totalRows && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={onLoadMore}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10"
            >
              Afficher plus ({visibleCount} / {totalRows})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}